import { type FC, useCallback, useMemo, useState } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { type LinkItemProps } from '@vakantiesnl/components/src/molecules/LinkList';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useGetLogoutFn } from '@vakantiesnl/services/src/hooks/useGetLogoutFn';
import { type UserProfile } from '@vakantiesnl/services/src/user';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useStyles } from './AccountWrapper.style';

const UserMenuMobileDrawer = dynamic(
	() =>
		import('@vakantiesnl/components/src/molecules/UserMenuMobileDrawer/UserMenuMobileDrawer').then((module) => ({
			default: module.UserMenuMobileDrawer,
		})),
	{
		ssr: false,
	},
);

type Props = {
	links: LinkItemProps[];
	userData?: UserProfile;
};

export const BaseMenu: FC<Props> = ({ links, userData }) => {
	const { isDesktop } = useBreakpoints();
	const { asPath, isReady } = useRouter();
	const { classes } = useStyles();
	const [menuDrawerIsOpen, setMenuDrawerIsOpen] = useState(false);

	const activeMenuItem = useMemo(() => {
		if (isReady) {
			const matchingItem = links.find((link) => asPath === link?.href);

			if (matchingItem) {
				return matchingItem;
			}

			// Try to match by removing the last section of the asPath in case it's a nested page
			const matchingSubItem = links.find((link) => {
				const asPathParent = asPath.substring(0, asPath.lastIndexOf('/'));
				return asPathParent === link.href;
			});

			return matchingSubItem;
		}
	}, [asPath, isReady, links]);

	const handleLogout = useGetLogoutFn();

	const toggleMenuDrawer = useCallback(() => setMenuDrawerIsOpen((isOpen) => !isOpen), []);

	const renderLogoutButton = useCallback(
		(linkItem: LinkItemProps) => {
			return (
				<Button
					onClick={handleLogout}
					className={classes.linkButton}
					active={activeMenuItem === linkItem}
					variant="tertiary"
				>
					{linkItem.icon && <Icon type={linkItem.icon} />}
					<Typography variant="labelMd" as="span">
						{linkItem.text}
					</Typography>
				</Button>
			);
		},
		[handleLogout, activeMenuItem, classes.linkButton],
	);

	const renderTextLink = useCallback(
		(linkItem: LinkItemProps) => {
			return (
				<Button
					href={linkItem.href}
					asText={true}
					className={classes.linkButton}
					active={activeMenuItem === linkItem}
					variant="tertiary"
				>
					{linkItem.icon && <Icon type={linkItem.icon} />}
					<Typography variant="labelMd" as="span">
						{linkItem.text}
					</Typography>
				</Button>
			);
		},
		[activeMenuItem, classes.linkButton],
	);

	return (
		<>
			{!isDesktop ? (
				<Button
					variant="secondary"
					title={activeMenuItem?.text}
					onClick={toggleMenuDrawer}
					leadingIcon={activeMenuItem?.icon}
					trailingIcon="chevronDown"
					className={classes.mobileButton}
				>
					<Typography variant="bodyMd" as="span" className={classes.mobileButtonText}>
						{activeMenuItem?.text}
					</Typography>
				</Button>
			) : (
				<div>
					{!!links.length && (
						<ul>
							{links.map((linkItem, index) => {
								const isLogoutLink = linkItem.icon === 'logout';

								return (
									<li className={classes.linkItem} key={index}>
										{isLogoutLink ? renderLogoutButton(linkItem) : renderTextLink(linkItem)}
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
			{menuDrawerIsOpen && !isDesktop && links.length && (
				<UserMenuMobileDrawer
					open={menuDrawerIsOpen}
					onDrawerClose={toggleMenuDrawer}
					links={links}
					userInfo={userData}
				/>
			)}
		</>
	);
};
